import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { motion } from "framer-motion";
import MainContent from "./components/MainContent";
import ChatPage from "./components/ChatPage";
import LoginPage from "./components/LoginPage";
import SignUpPage from "./components/SignUpPage";
import LandingPage from "./components/LandingPage";
import Sidebar from "./components/Sidebar";
import Dashboard from "./components/Dashboard";
import SubscriptionPage from "./components/SubscriptionPage";
import ProtectedRoute from "./components/ProtectedRoute";
import { PageTransition } from "./components/PageTransition";
import { AnimatePresence } from "framer-motion";

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  // localStorage에서 상태 복원
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem("isLoggedIn") === "true";
  });
  const [userRole, setUserRole] = useState(() => {
    return localStorage.getItem("userRole") || "user";
  });
  const [selectedEngine, setSelectedEngine] = useState(() => {
    // 현재 경로에서 엔진 타입 추출
    if (location.pathname.includes("/daily-news-analysis")) return "daily-news-analysis";
    if (location.pathname.includes("/press-release-writing")) return "press-release-writing";
    if (location.pathname.includes("/digital-news-writing")) return "digital-news-writing";
    // localStorage에서 복원
    return localStorage.getItem("selectedEngine") || "daily-news-analysis";
  });
  const [currentProject, setCurrentProject] = useState({
    title: "아키텍쳐",
    isStarred: false,
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const sidebarRef = useRef(null);

  // 엔진 변경 시 프로젝트 제목 업데이트 및 localStorage 저장
  useEffect(() => {
    setCurrentProject((prev) => ({
      ...prev,
      title: `${
        selectedEngine.charAt(0).toUpperCase() + selectedEngine.slice(1)
      } AI 교열 서비스`,
    }));
    localStorage.setItem("selectedEngine", selectedEngine);
  }, [selectedEngine]);

  // 로그인 상태 변경 시 localStorage 저장
  useEffect(() => {
    localStorage.setItem("isLoggedIn", isLoggedIn);
  }, [isLoggedIn]);

  // 사용자 역할 변경 시 localStorage 저장
  useEffect(() => {
    localStorage.setItem("userRole", userRole);
  }, [userRole]);

  const toggleStar = () => {
    setCurrentProject((prev) => ({
      ...prev,
      isStarred: !prev.isStarred,
    }));
  };

  const handleStartChat = (message) => {
    console.log("handleStartChat called with:", message);

    // 새 대화 ID 생성 (엔진_타임스탬프 형식)
    const conversationId = `${selectedEngine}_${Date.now()}`;
    console.log("새 대화 ID 생성:", conversationId);

    // localStorage에 임시 저장 (페이지 전환 중 데이터 보존)
    localStorage.setItem("pendingMessage", message);
    localStorage.setItem("pendingConversationId", conversationId);

    // conversationId를 포함한 URL로 이동
    const enginePath = selectedEngine.toLowerCase();
    navigate(`/${enginePath}/chat/${conversationId}`, {
      state: { initialMessage: message },
    });

    console.log(
      "대화 페이지로 이동:",
      `/${enginePath}/chat/${conversationId}`
    );
  };

  const handleBackToMain = () => {
    const enginePath = selectedEngine.toLowerCase();
    navigate(`/${enginePath}`);
  };

  const handleLogout = async () => {
    console.log("App.jsx handleLogout 호출됨");
    try {
      // Cognito 로그아웃
      const authService = (await import("./services/authService")).default;
      await authService.signOut();
      console.log("Cognito 로그아웃 완료");
    } catch (error) {
      console.error("로그아웃 오류:", error);
    }

    // 로컬 상태 및 스토리지 초기화
    setIsLoggedIn(false);
    setUserRole("user");
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("userRole");
    localStorage.removeItem("selectedEngine");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("authToken");
    localStorage.removeItem("idToken");
    localStorage.removeItem("refreshToken");

    // Header에 사용자 정보 업데이트 알림
    window.dispatchEvent(new CustomEvent("userInfoUpdated"));

    // 현재 페이지가 랜딩 페이지가 아닌 경우에만 랜딩 페이지로 이동
    if (location.pathname !== "/") {
      console.log("랜딩 페이지로 이동");
      navigate("/");
    } else {
      console.log("현재 랜딩 페이지 유지");
    }
  };

  const handleLogin = (role = "user") => {
    setIsLoggedIn(true);
    setUserRole(role);
    // location.state에서 엔진 정보 가져오기
    const engine = location.state?.engine || selectedEngine;
    setSelectedEngine(engine);
    // 랜딩 페이지로 이동
    navigate("/");
  };

  const handleSelectEngine = (engine) => {
    setSelectedEngine(engine);
    setCurrentProject((prev) => ({
      ...prev,
      title: `${
        engine.charAt(0).toUpperCase() + engine.slice(1)
      } AI 교열 서비스`,
    }));

    // 로그인 상태 확인
    if (isLoggedIn) {
      // 로그인되어 있으면 해당 엔진 페이지로 이동
      const enginePath = engine.toLowerCase();
      navigate(`/${enginePath}`);
    } else {
      // 로그인되어 있지 않으면 로그인 페이지로
      navigate("/login", { state: { engine } });
    }
  };

  const handleSignUp = () => {
    setIsLoggedIn(true);
    const enginePath = selectedEngine.toLowerCase();
    navigate(`/${enginePath}`);
  };

  const handleGoToSignUp = () => {
    navigate("/signup");
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const handleBackToLanding = () => {
    navigate("/");
  };

  const handleTitleUpdate = (newTitle) => {
    setCurrentProject((prev) => ({
      ...prev,
      title: newTitle,
    }));
    console.log("앱 제목 업데이트됨:", newTitle);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const handleNewConversation = () => {
    // 사이드바의 대화 목록 새로고침
    if (sidebarRef.current && sidebarRef.current.loadConversations) {
      sidebarRef.current.loadConversations();
    }
  };

  const handleDashboard = (engine) => {
    const enginePath = engine
      ? engine.toLowerCase()
      : selectedEngine.toLowerCase();
    navigate(`/${enginePath}/dashboard`);
  };

  const handleBackFromDashboard = (engine) => {
    const enginePath = engine
      ? engine.toLowerCase()
      : selectedEngine.toLowerCase();
    navigate(`/${enginePath}/chat`);
  };

  const handleSubscription = () => {
    navigate("/subscription");
  };

  // 사이드바를 보여줄 페이지 확인 (랜딩, 로그인, 회원가입, 대시보드, 구독 제외)
  const showSidebar =
    !["/", "/login", "/signup", "/subscription"].includes(location.pathname) &&
    !location.pathname.includes("/dashboard");

  return (
    <div
      className="flex w-full overflow-x-clip"
      style={{
        minHeight: "100dvh",
        backgroundColor: "hsl(var(--bg-100))",
        color: "hsl(var(--text-100))",
      }}
    >
      {/* Sidebar - show on all pages except landing, login, signup */}
      {showSidebar && (
        <Sidebar
          ref={sidebarRef}
          selectedEngine={selectedEngine}
          isOpen={isSidebarOpen}
          onToggle={toggleSidebar}
        />
      )}

      <motion.div
        className="min-h-full w-full min-w-0 flex-1"
        animate={{
          marginLeft: showSidebar && isSidebarOpen ? 288 : 0,
        }}
        transition={{
          type: "tween",
          ease: "easeInOut",
          duration: 0.2,
        }}
      >
        <AnimatePresence mode="wait">
          <Routes
            location={location}
            key={location.pathname.split("/").slice(0, 3).join("/")}
          >
            <Route
              path="/"
              element={
                <PageTransition pageKey="landing">
                  <LandingPage
                    onSelectEngine={handleSelectEngine}
                    onLogin={handleLogin}
                    onLogout={handleLogout}
                  />
                </PageTransition>
              }
            />
            <Route
              path="/login"
              element={
                <LoginPage
                  onLogin={handleLogin}
                  onGoToSignUp={handleGoToSignUp}
                  selectedEngine={location.state?.engine || selectedEngine}
                />
              }
            />
            <Route
              path="/signup"
              element={
                <SignUpPage
                  onSignUp={handleSignUp}
                  onBackToLogin={handleBackToLogin}
                />
              }
            />
            <Route
              path="/t5/chat/:conversationId?"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="chat-t5">
                    <ChatPage
                      initialMessage={location.state?.initialMessage}
                      userRole={userRole}
                      selectedEngine="press-release-writing"
                      onLogout={handleLogout}
                      onBackToLanding={handleBackToLanding}
                      onTitleUpdate={handleTitleUpdate}
                      onToggleSidebar={toggleSidebar}
                      isSidebarOpen={isSidebarOpen}
                      onNewConversation={handleNewConversation}
                      onDashboard={() => handleDashboard("press-release-writing")}
                      onSubscription={handleSubscription}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/h8/chat/:conversationId?"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="chat-h8">
                    <ChatPage
                      initialMessage={location.state?.initialMessage}
                      userRole={userRole}
                      selectedEngine="digital-news-writing"
                      onLogout={handleLogout}
                      onBackToLanding={handleBackToLanding}
                      onTitleUpdate={handleTitleUpdate}
                      onToggleSidebar={toggleSidebar}
                      isSidebarOpen={isSidebarOpen}
                      onNewConversation={handleNewConversation}
                      onDashboard={() => handleDashboard("digital-news-writing")}
                      onSubscription={handleSubscription}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            {/* Routes for Daily News Analysis/Press Release Writing/Digital News Writing */}
            <Route
              path="/daily-news-analysis/chat/:conversationId?"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="chat-daily-news-analysis">
                    <ChatPage
                      initialMessage={location.state?.initialMessage}
                      userRole={userRole}
                      selectedEngine="daily-news-analysis"
                      onLogout={handleLogout}
                      onBackToLanding={handleBackToLanding}
                      onTitleUpdate={handleTitleUpdate}
                      onToggleSidebar={toggleSidebar}
                      isSidebarOpen={isSidebarOpen}
                      onNewConversation={handleNewConversation}
                      onDashboard={() => handleDashboard("daily-news-analysis")}
                      onSubscription={handleSubscription}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/press-release-writing/chat/:conversationId?"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="chat-press-release-writing">
                    <ChatPage
                      initialMessage={location.state?.initialMessage}
                      userRole={userRole}
                      selectedEngine="press-release-writing"
                      onLogout={handleLogout}
                      onBackToLanding={handleBackToLanding}
                      onTitleUpdate={handleTitleUpdate}
                      onToggleSidebar={toggleSidebar}
                      isSidebarOpen={isSidebarOpen}
                      onNewConversation={handleNewConversation}
                      onDashboard={() => handleDashboard("press-release-writing")}
                      onSubscription={handleSubscription}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/digital-news-writing/chat/:conversationId?"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="chat-digital-news-writing">
                    <ChatPage
                      initialMessage={location.state?.initialMessage}
                      userRole={userRole}
                      selectedEngine="digital-news-writing"
                      onLogout={handleLogout}
                      onBackToLanding={handleBackToLanding}
                      onTitleUpdate={handleTitleUpdate}
                      onToggleSidebar={toggleSidebar}
                      isSidebarOpen={isSidebarOpen}
                      onNewConversation={handleNewConversation}
                      onDashboard={() => handleDashboard("digital-news-writing")}
                      onSubscription={handleSubscription}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/t5"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="main-t5">
                    <MainContent
                      project={currentProject}
                      userRole={userRole}
                      selectedEngine="press-release-writing"
                      onToggleStar={toggleStar}
                      onStartChat={handleStartChat}
                      onLogout={handleLogout}
                      onBackToLanding={handleBackToLanding}
                      onToggleSidebar={toggleSidebar}
                      isSidebarOpen={isSidebarOpen}
                      onDashboard={() => handleDashboard("press-release-writing")}
                      onSubscription={handleSubscription}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/h8"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="main-h8">
                    <MainContent
                      project={currentProject}
                      userRole={userRole}
                      selectedEngine="digital-news-writing"
                      onToggleStar={toggleStar}
                      onStartChat={handleStartChat}
                      onLogout={handleLogout}
                      onBackToLanding={handleBackToLanding}
                      onToggleSidebar={toggleSidebar}
                      isSidebarOpen={isSidebarOpen}
                      onDashboard={() => handleDashboard("digital-news-writing")}
                      onSubscription={handleSubscription}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            {/* Main routes for Daily News Analysis/Press Release Writing/Digital News Writing */}
            <Route
              path="/daily-news-analysis"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="main-daily-news-analysis">
                    <MainContent
                      project={currentProject}
                      userRole={userRole}
                      selectedEngine="daily-news-analysis"
                      onToggleStar={toggleStar}
                      onStartChat={handleStartChat}
                      onLogout={handleLogout}
                      onBackToLanding={handleBackToLanding}
                      onToggleSidebar={toggleSidebar}
                      isSidebarOpen={isSidebarOpen}
                      onDashboard={() => handleDashboard("daily-news-analysis")}
                      onSubscription={handleSubscription}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/press-release-writing"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="main-press-release-writing">
                    <MainContent
                      project={currentProject}
                      userRole={userRole}
                      selectedEngine="press-release-writing"
                      onToggleStar={toggleStar}
                      onStartChat={handleStartChat}
                      onLogout={handleLogout}
                      onBackToLanding={handleBackToLanding}
                      onToggleSidebar={toggleSidebar}
                      isSidebarOpen={isSidebarOpen}
                      onDashboard={() => handleDashboard("press-release-writing")}
                      onSubscription={handleSubscription}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/digital-news-writing"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="main-digital-news-writing">
                    <MainContent
                      project={currentProject}
                      userRole={userRole}
                      selectedEngine="digital-news-writing"
                      onToggleStar={toggleStar}
                      onStartChat={handleStartChat}
                      onLogout={handleLogout}
                      onBackToLanding={handleBackToLanding}
                      onToggleSidebar={toggleSidebar}
                      isSidebarOpen={isSidebarOpen}
                      onDashboard={() => handleDashboard("digital-news-writing")}
                      onSubscription={handleSubscription}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/t5/dashboard"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="dashboard-t5">
                    <Dashboard
                      selectedEngine="press-release-writing"
                      onBack={() => handleBackFromDashboard("press-release-writing")}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/h8/dashboard"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="dashboard-h8">
                    <Dashboard
                      selectedEngine="digital-news-writing"
                      onBack={() => handleBackFromDashboard("digital-news-writing")}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            {/* Dashboard Routes */}
            <Route
              path="/daily-news-analysis/dashboard"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="dashboard-daily-news-analysis">
                    <Dashboard
                      selectedEngine="daily-news-analysis"
                      onBack={() => handleBackFromDashboard("daily-news-analysis")}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/press-release-writing/dashboard"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="dashboard-press-release-writing">
                    <Dashboard
                      selectedEngine="press-release-writing"
                      onBack={() => handleBackFromDashboard("press-release-writing")}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/digital-news-writing/dashboard"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="dashboard-digital-news-writing">
                    <Dashboard
                      selectedEngine="digital-news-writing"
                      onBack={() => handleBackFromDashboard("digital-news-writing")}
                    />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            {/* Subscription Page */}
            <Route
              path="/subscription"
              element={
                <ProtectedRoute>
                  <PageTransition pageKey="subscription">
                    <SubscriptionPage />
                  </PageTransition>
                </ProtectedRoute>
              }
            />
            {/* 기본 리다이렉트 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
